import type { MapLook, Mood } from "../maps";

// Map moods (#179): Realistic's color grade per map, the light it's meant to
// feel like. Each mood is a small grade (lift, gain and gamma per channel, an
// S-curve and saturation) baked into a 3D look-up table that the post chain
// applies in display space, after tone mapping (`render/composer.ts`). Plain
// numbers here, no three.js, so the grades are testable.

export type { Mood };

type Rgb = readonly [number, number, number];

export interface MoodGrade {
  /** What black comes out as, per channel: a tint in the shadows. */
  lift: Rgb;
  /** What white comes out as, per channel: a tint in the highlights. */
  gain: Rgb;
  /** Above 1 brightens that channel's mids. */
  gamma: Rgb;
  /** How much of an S-curve to blend in, 0 .. 1. */
  contrast: number;
  /** Color about each pixel's own grey: 1 leaves it alone. */
  saturation: number;
}

/** Changes nothing. */
export const NEUTRAL: MoodGrade = { lift: [0, 0, 0], gain: [1, 1, 1], gamma: [1, 1, 1], contrast: 0, saturation: 1 };

export const MOODS: Record<Mood, MoodGrade> = {
  // Plain daylight: a touch of contrast and cool in the deepest shadows.
  noon: { lift: [0.01, 0.01, 0.018], gain: [1, 1, 0.98], gamma: [1, 1, 1], contrast: 0.12, saturation: 1 },
  // Late sun over the beach: amber highlights against teal shade.
  golden: { lift: [0, 0.016, 0.032], gain: [1, 0.95, 0.84], gamma: [1.04, 1, 0.96], contrast: 0.16, saturation: 1.05 },
  // A grey city day: washed out, cool and flat, raised blacks.
  overcast: { lift: [0.02, 0.026, 0.042], gain: [0.94, 0.97, 1], gamma: [0.98, 1, 1.02], contrast: 0.1, saturation: 0.8 },
  // Sunset at the fair: violet shadows, peach highlights, punchy.
  dusk: { lift: [0.04, 0, 0.062], gain: [1, 0.9, 0.8], gamma: [1, 0.97, 1], contrast: 0.18, saturation: 1.08 },
  // Indoors under lamps: warm mids, green-teal shade, crushed blacks.
  tungsten: { lift: [0, 0.014, 0.02], gain: [1, 0.9, 0.72], gamma: [1.03, 1, 0.95], contrast: 0.2, saturation: 0.95 },
};

/** A map's mood: its own, or noon outdoors and tungsten indoors. */
export function moodFor(look: MapLook): Mood {
  return look.mood ?? (look.indoor ? "tungsten" : "noon");
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** One display-space color through a grade, each channel 0 .. 1. */
export function gradeColor(m: MoodGrade, r: number, g: number, b: number): [number, number, number] {
  const out: [number, number, number] = [r, g, b];
  for (let i = 0; i < 3; i++) {
    let v = m.lift[i]! + out[i]! * (m.gain[i]! - m.lift[i]!);
    v = Math.pow(clamp01(v), 1 / m.gamma[i]!);
    v += (v * v * (3 - 2 * v) - v) * m.contrast;
    out[i] = v;
  }
  const luma = 0.2126 * out[0] + 0.7152 * out[1] + 0.0722 * out[2];
  for (let i = 0; i < 3; i++) out[i] = clamp01(luma + (out[i]! - luma) * m.saturation);
  return out;
}

/**
 * The grade as a `size`³ RGBA table, red fastest, then green, then blue
 * (what three's `Data3DTexture` and `LUTPass` read).
 */
export function buildLut(m: MoodGrade, size = 32): Uint8Array {
  const data = new Uint8Array(size * size * size * 4);
  const step = 1 / (size - 1);
  let i = 0;
  for (let b = 0; b < size; b++)
    for (let g = 0; g < size; g++)
      for (let r = 0; r < size; r++) {
        const c = gradeColor(m, r * step, g * step, b * step);
        data[i++] = Math.round(c[0] * 255);
        data[i++] = Math.round(c[1] * 255);
        data[i++] = Math.round(c[2] * 255);
        data[i++] = 255;
      }
  return data;
}
